import { useState } from 'react'
import { Upload, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  useUploadContent,
  usePromoteToRepository,
  useLinkRepositoryAssetToProduct,
} from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'

interface RepositoryDefaults {
  section: import('@/types').RepositorySection
  asset_type: string
  productBinding?: { product_id: string; role: string }
}

interface UploadContentModalProps {
  open: boolean
  // Either onOpenChange (legacy) or onClose (newer call-sites) is accepted.
  onOpenChange?: (open: boolean) => void
  onClose?: () => void
  linkedEntityId?: string
  repositoryDefaults?: RepositoryDefaults
}

export function UploadContentModal({
  open,
  onOpenChange,
  onClose,
  linkedEntityId,
  repositoryDefaults,
}: UploadContentModalProps) {
  const { toast } = useToast()
  const uploadMutation = useUploadContent()
  const promoteMutation = usePromoteToRepository()
  const linkProductMutation = useLinkRepositoryAssetToProduct()

  const close = () => {
    if (onClose) onClose()
    if (onOpenChange) onOpenChange(false)
  }
  const setOpen = (next: boolean) => {
    if (!next) close()
    else onOpenChange?.(true)
  }

  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      if (!title) {
        setTitle(selectedFile.name)
      }
      if (errors.file) {
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors.file
          return newErrors
        })
      }
    }
  }

  const handleRemoveFile = () => {
    setFile(null)
    const fileInput = document.getElementById('file-upload') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!file) {
      newErrors.file = 'Please select a file to upload'
    } else if (file.size > 50 * 1024 * 1024) {
      newErrors.file = 'File size must not exceed 50MB'
    }

    if (!title?.trim()) {
      newErrors.title = 'Title is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please correct the errors before uploading',
        variant: 'destructive',
      })
      return
    }

    if (!file) return

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('title', title.trim())
      if (description.trim()) {
        formData.append('description', description.trim())
      }
      if (linkedEntityId) {
        formData.append('linked_entity_id', linkedEntityId)
      }

      const { data: uploaded } = await uploadMutation.mutateAsync(formData)

      // If invoked from the Repository, promote the new content version into
      // the curated library and (optionally) bind it to a product.
      if (repositoryDefaults && uploaded?.id) {
        await promoteMutation.mutateAsync({
          contentVersionId: uploaded.id,
          payload: {
            repository_section: repositoryDefaults.section,
            asset_type: repositoryDefaults.asset_type,
          },
        })
        if (repositoryDefaults.productBinding) {
          await linkProductMutation.mutateAsync({
            id: uploaded.id,
            payload: repositoryDefaults.productBinding,
          })
        }
      }

      toast({
        title: 'Success',
        description: repositoryDefaults
          ? 'Document added to the repository'
          : 'File uploaded successfully',
      })

      // Reset form
      setFile(null)
      setTitle('')
      setDescription('')
      setErrors({})
      const fileInput = document.getElementById('file-upload') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
      close()
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to upload file'),
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: string, value: string) => {
    if (field === 'title') setTitle(value)
    if (field === 'description') setDescription(value)

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" aria-describedby="upload-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" aria-hidden="true" />
            Upload File
          </DialogTitle>
          <DialogDescription id="upload-description">
            Upload a file to the content library. Supported formats: PDF, images, Word, Excel, and
            more.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Input */}
          <div>
            <Label htmlFor="file-upload" className="required">
              File *
            </Label>
            <div className="mt-2">
              {!file ? (
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-10 h-10 mb-3 text-muted-foreground" aria-hidden="true" />
                      <p className="mb-2 text-sm text-muted-foreground">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground">Max file size: 50MB</p>
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      aria-required="true"
                      aria-invalid={!!errors.file}
                      aria-describedby={errors.file ? 'file-error' : undefined}
                    />
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 border rounded-md">
                  <div className="flex items-center gap-3">
                    <Upload className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
                    <div>
                      <div className="text-sm font-medium">{file.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              )}
              {errors.file && (
                <p id="file-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.file}
                </p>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="upload-title" className="required">
              Title *
            </Label>
            <Input
              id="upload-title"
              value={title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter file title"
              aria-required="true"
              aria-invalid={!!errors.title}
              aria-describedby={errors.title ? 'title-error' : undefined}
              className="mt-2"
            />
            {errors.title && (
              <p id="title-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.title}
              </p>
            )}
          </div>

          {/* Document Type */}
          <div>
            <Label htmlFor="upload-description">Document Type</Label>
            <select
              id="upload-description"
              value={description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-2"
              aria-label="Document type"
            >
              <option value="">Select document type...</option>
              <option value="PO">PO</option>
              <option value="Signed Quote">Signed Quote</option>
              <option value="SoW">SoW</option>
              <option value="Screen Shot">Screen Shot</option>
              <option value="MSA">MSA</option>
              <option value="NDA">NDA</option>
              <option value="3rd Party Invoice">3rd Party Invoice</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={close}
              disabled={
                uploadMutation.isPending ||
                promoteMutation.isPending ||
                linkProductMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                uploadMutation.isPending ||
                promoteMutation.isPending ||
                linkProductMutation.isPending
              }
            >
              {uploadMutation.isPending || promoteMutation.isPending
                ? repositoryDefaults
                  ? 'Adding to repository…'
                  : 'Uploading…'
                : repositoryDefaults
                  ? 'Add to repository'
                  : 'Upload File'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
